import React, { useEffect, useMemo, useState } from 'react'
import {
  ActionButtons,
  Button,
  DataTable,
  InputField,
  SearchWithFilter,
  ToggleSwitch,
  type DataTableColumn,
  type DataTableSortDirection,
  type FilterSelection,
} from '@enamelai/design-system'
import { Icon as CheckmarkIcon } from '@enamelai/design-system/icons/Checkmark'
import { Icon as CloseIcon } from '@enamelai/design-system/icons/Close'
import { DeleteConfirmationModal } from '@components/layout/Modals'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useDebouncedValue } from '@hooks/useDebouncedValue'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import { SettingStatus } from '@dataTypes/settingStatus.type'
import { SortDirection } from '@constants/Common/sort-direction.constant'
import SettingsRowActions from '../../shared/SettingsRowActions.component'
import { SETTINGS_PAGE_SIZE_OPTIONS } from '../../shared/settingsTable'
import useDiscounts from './useDiscounts'
import {
  DISCOUNT_FILTER_LABELS,
  DISCOUNT_FILTERS,
} from '../shared/billingSettings.constants'
import { DisplayDiscount } from '../shared/billingSettings.types'
import './DiscountsStyle.css'

const DISCOUNTS_SEARCH_DEBOUNCE_MS = 300
const NEW_ROW_ID = 'new-discount'
const EMPTY_FILTERS: FilterSelection = { status: [] }

type DiscountRow = {
  id: string
  discountId: string
  status: string
  description: string
  percentage: string
  isEditing: boolean
}

const formatDiscountPercentDisplay = (value: number | string): string => {
  const n = Number(value)
  if (Number.isNaN(n)) return String(value)
  return String(parseFloat(n.toFixed(10)))
}

const Discounts = (): React.ReactElement => {
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const { setHeaderButtons } = useSettingsHeader()
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebouncedValue(
    searchQuery,
    DISCOUNTS_SEARCH_DEBOUNCE_MS,
  )
  const [filters, setFilters] = useState<FilterSelection>(EMPTY_FILTERS)
  const [sort, setSort] = useState<{
    columnId: string
    direction: DataTableSortDirection
  }>({ columnId: 'description', direction: 'asc' })
  const [pendingDelete, setPendingDelete] = useState<DiscountRow | null>(null)

  const hooks = useDiscounts(
    debouncedSearchQuery,
    filters,
    showToast,
    showLoading,
    hideLoading,
  )
  const { reset, listLoading } = hooks
  useSettingsSubpageLoading(listLoading)
  const hasSearchOrFilter =
    searchQuery.trim().length > 0 ||
    Object.values(filters).some((values) => (values?.length ?? 0) > 0)
  const isTrulyEmpty =
    !listLoading &&
    !hooks.newEntry &&
    hooks.discounts.length === 0 &&
    !hasSearchOrFilter

  useEffect(() => {
    reset()
  }, [reset])

  useEffect(() => {
    setHeaderButtons(
      <>
        <SearchWithFilter
          className="discounts-search"
          placeholder="Search"
          value={searchQuery}
          onChange={setSearchQuery}
          filters={DISCOUNT_FILTERS}
          filterLabels={DISCOUNT_FILTER_LABELS}
          selectedFilters={filters}
          onFilterChange={setFilters}
          showResetButton
          onReset={() => {
            setSearchQuery('')
            setFilters(EMPTY_FILTERS)
          }}
        />
        {!isTrulyEmpty && (
          <Button
            className="settings-ds-button"
            variant="primary"
            size="md"
            type="button"
            onClick={hooks.handleAddNew}
            disabled={!!hooks.newEntry}
          >
            Add discount
          </Button>
        )}
      </>,
    )
    return () => setHeaderButtons(null)
  }, [
    hooks.handleAddNew,
    hooks.newEntry,
    isTrulyEmpty,
    searchQuery,
    filters,
    setHeaderButtons,
  ])

  const rows: DiscountRow[] = useMemo(
    () =>
      hooks.discounts.map((discount: DisplayDiscount) => ({
        id: discount.discount_id || NEW_ROW_ID,
        discountId: discount.discount_id,
        status: discount.status,
        description: discount.description,
        percentage: String(discount.discount_percentage ?? ''),
        isEditing: discount.isEditing,
      })),
    [hooks.discounts],
  )

  const handleSort = (columnId: string): void => {
    const direction: DataTableSortDirection =
      sort.columnId === columnId && sort.direction === 'asc' ? 'desc' : 'asc'
    setSort({ columnId, direction })
    hooks.handleTableSortChange({
      key: columnId,
      direction: direction === 'asc' ? SortDirection.ASC : SortDirection.DESC,
    })
  }

  const columns: DataTableColumn<DiscountRow>[] = [
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      width: 88,
      renderCell: (row) => (
        <ToggleSwitch
          aria-label={`${row.description || 'Discount'} status`}
          checked={row.status === SettingStatus.Enabled}
          disabled={row.isEditing}
          onChange={(checked) =>
            hooks.handleToggleStatus(row.discountId, checked)
          }
        />
      ),
    },
    {
      id: 'description',
      label: 'Discount type',
      sortable: true,
      renderCell: (row) =>
        row.isEditing ? (
          <InputField
            className="discounts-inline-input"
            variant="text"
            aria-label="Discount type"
            placeholder="Enter discount type"
            clearable={false}
            fullWidth
            value={row.description}
            onChange={(e) =>
              hooks.handleChange(row.discountId, 'description', e.target.value)
            }
          />
        ) : (
          row.description
        ),
    },
    {
      id: 'discount_percentage',
      label: 'Discount (%)',
      sortable: true,
      width: 240,
      renderCell: (row) =>
        row.isEditing ? (
          <InputField
            className="discounts-inline-input discounts-inline-input--percent"
            variant="text"
            inputMode="decimal"
            aria-label="Discount percentage"
            placeholder="0"
            clearable={false}
            fullWidth
            value={row.percentage}
            onChange={(e) =>
              hooks.handleChange(
                row.discountId,
                'discount_percentage',
                e.target.value.replace(/[^\d.]/g, ''),
              )
            }
          />
        ) : (
          formatDiscountPercentDisplay(row.percentage)
        ),
    },
  ]

  const tableState = listLoading
    ? 'loading'
    : rows.length === 0
      ? 'empty'
      : 'default'

  return (
    <div className="settings-accounting-discounts">
      <DataTable<DiscountRow>
        className="discounts-table"
        columns={columns}
        rows={rows}
        state={tableState}
        emptyState={{
          title: 'No discounts found',
          description: hasSearchOrFilter
            ? 'No discounts match your search.'
            : "You don't have any discounts on record yet.",
          actionLabel: !hasSearchOrFilter ? 'Add discount' : undefined,
          onAction: !hasSearchOrFilter ? hooks.handleAddNew : undefined,
        }}
        sortColumnId={sort.columnId}
        sortDirection={sort.direction}
        onSort={handleSort}
        actionsLabel="Actions"
        renderActions={(row) =>
          row.isEditing ? (
            <ActionButtons
              size="sm"
              actions={[
                {
                  id: 'save',
                  label: 'Save',
                  icon: (
                    <CheckmarkIcon
                      className="ds-action-button__svg"
                      aria-hidden="true"
                    />
                  ),
                  tone: 'primary',
                  variant: 'solid',
                  size: 'sm',
                  onClick: () => {
                    void hooks.handleSave(row.discountId)
                  },
                },
                {
                  id: 'cancel',
                  label: 'Cancel',
                  icon: (
                    <CloseIcon
                      className="ds-action-button__svg"
                      aria-hidden="true"
                    />
                  ),
                  tone: 'danger',
                  variant: 'outline',
                  size: 'sm',
                  onClick: () => hooks.handleCancel(row.discountId),
                },
              ]}
            />
          ) : (
            <SettingsRowActions
              onEdit={() => hooks.handleEdit(row.discountId)}
              onDelete={() => setPendingDelete(row)}
            />
          )
        }
        pagination={{
          currentPage: hooks.currentPage,
          totalPages: Math.max(
            1,
            Math.ceil(hooks.discountCount / hooks.pageSize),
          ),
          pageSize: hooks.pageSize,
          pageSizeOptions: SETTINGS_PAGE_SIZE_OPTIONS,
          onPageChange: hooks.handlePageChange,
          onPageSizeChange: hooks.handlePageSizeChange,
        }}
      />

      <DeleteConfirmationModal
        show={!!pendingDelete}
        onHide={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) void hooks.handleDelete(pendingDelete.discountId)
          setPendingDelete(null)
        }}
        entityName={pendingDelete?.description ?? ''}
        title="Delete discount?"
        message="Are you sure you want to delete the discount"
        confirmButtonText="Delete"
      />
    </div>
  )
}

export default Discounts
